import {calculateDistanceByMapId} from "../../../../travian/Calculator/distance";
import {reverseCalculate} from "../../../../travian/Calculator/travelTime";
import translate from "../../../../translate";
import React, {useState} from "react";
import Editable from "../../ui/editable";
import {Column, ColumnGrid} from "../../ui/spectre/grid";
import Sizes from "../../ui/spectre/common/sizes";
import {FormatDateTime, ParseDateTime} from "../../../../helper/dateTime";
import CoalesceFunction from "../../../../helper/coalesceFunction";
import useEffectExceptInitial from "../../../hooks/useEffectExceptInitial";
import {FormatDecimalNumber, FormatFullPercent, ParseFullPercent} from "../../../../helper/numbers";
import {Kingdoms, Legends} from "../travianVersionHelper";
import {Dropdown, DropdownElement} from "../../ui/spectre/dropdown";
import {ButtonTypes} from "../../ui/spectre/buttons";

/**
 * @param start
 * @param target
 * @param onChange
 * @param params {{ arrival, firstSeen, lastUnseen, artefactBonus, bootsBonus, standardBonus }}
 * @returns {JSX.Element}
 * @constructor
 */
export function SpeedAnalysis({start, target, onChange, ...params}) {
    const [arrival, setArrival] = useState(params.arrival);
    const [firstSeen, setFirstSeen] = useState(params.firstSeen);
    const [lastUnseen, setLastUnseen] = useState(params.lastUnseen);
    const [artefactBonus, setArtefactBonus] = useState(params.artefactBonus ?? 1);
    const [bootsBonus, setBootsBonus] = useState(params.bootsBonus ?? 0);
    const [standardBonus, setStandardBonus] = useState(params.standardBonus ?? 0);

    useEffectExceptInitial(
        () => {
            onChange({ arrival, firstSeen, lastUnseen });
        },
        [arrival, firstSeen, lastUnseen]
    );

    const calculationParameters = {
        distance: calculateDistanceByMapId(start, target),
        possibleBaseSpeeds: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19],
        travelTimeMin: arrival - (firstSeen ?? Date.now() / 1000),
        travelTimeMax: arrival - (lastUnseen ?? (Date.now() / 1000 - 24 * 60 * 60)),
        tournamentSquareMin: 0,
        tournamentSquareMax: 20,
        artefact: artefactBonus,
        heroBoots: bootsBonus,
        heroStandard: standardBonus,
    };
    const data = reverseCalculate(calculationParameters);

    onChange = CoalesceFunction(onChange);

    // Make data nicely displayable
    const niceData = data.reduce(
        (carry, current) => {
            if (!carry[current.baseSpeed]) {
                carry[current.baseSpeed] = {
                    baseSpeed: current.baseSpeed,
                    min: current.tournamentSquare,
                    max: current.tournamentSquare
                };
            }
            if (carry[current.baseSpeed].min > current.tournamentSquare) {
                carry[current.baseSpeed].min = current.tournamentSquare;
            }
            if (carry[current.baseSpeed].max < current.tournamentSquare) {
                carry[current.baseSpeed].max = current.tournamentSquare;
            }
            return carry;
        },
        {}
    );
    const sortedNiceData = Object.values(niceData).sort((a, b) => a.baseSpeed - b.baseSpeed);

    return <div>
        <div className={'vspace-between-2'}>
            {translate('minitool/trav/speed_analysis/main_text')}
        </div>

        <div className={'vspace-between-2'}>
            <ColumnGrid size={Sizes.sm}>
                <Column sizes={[6]}>{translate('minitool/trav/speed_analysis/arrival')}</Column>
                <Column sizes={[6]}><Editable value={arrival} onChange={setArrival} formatter={FormatDateTime} parser={ParseDateTime} /></Column>

                <Column sizes={[6]}>{translate('minitool/trav/speed_analysis/first_seen')}</Column>
                <Column sizes={[6]}><Editable value={firstSeen} onChange={setFirstSeen} formatter={FormatDateTime} parser={ParseDateTime} /></Column>

                <Column sizes={[6]}>{translate('minitool/trav/speed_analysis/last_unseen')}</Column>
                <Column sizes={[6]}><Editable value={lastUnseen} onChange={setLastUnseen} formatter={FormatDateTime} parser={ParseDateTime} /></Column>
            </ColumnGrid>
        </div>

        <div className={'vspace-between-2'}>
            <ColumnGrid size={Sizes.sm}>
                <Legends>
                    <Column sizes={[6]}>{translate('minitool/trav/speed_analysis/artefact_bonus')}</Column>
                    <Column sizes={[6]}>
                        <Dropdown onChange={setArtefactBonus} initialValue={artefactBonus} buttonTypes={[ButtonTypes.small]}>
                            <DropdownElement value={2}>{FormatDecimalNumber(2)}x</DropdownElement>
                            <DropdownElement value={1.5}>{FormatDecimalNumber(1.5)}x</DropdownElement>
                            <DropdownElement value={1}>{FormatDecimalNumber(1)}x</DropdownElement>
                            <DropdownElement value={0.67}>{FormatDecimalNumber(0.67)}x</DropdownElement>
                            <DropdownElement value={0.5}>{FormatDecimalNumber(0.5)}x</DropdownElement>
                        </Dropdown>
                    </Column>
                </Legends>

                <Column sizes={[6]}>{translate('minitool/trav/speed_analysis/boots_bonus')}</Column>
                <Column sizes={[6]}><Editable value={bootsBonus} onChange={setBootsBonus} formatter={FormatFullPercent} parser={ParseFullPercent} /></Column>

                <Column sizes={[6]}>{translate('minitool/trav/speed_analysis/standard_bonus')}</Column>
                <Column sizes={[6]}><Editable value={standardBonus} onChange={setStandardBonus} formatter={FormatFullPercent} parser={ParseFullPercent} /></Column>
            </ColumnGrid>

            <div className={'text-small'}>
                <Legends>{translate('minitool/trav/speed_analysis/standard_info_tl')}</Legends>
                <Kingdoms>{translate('minitool/trav/speed_analysis/standard_info_tk')}</Kingdoms>
            </div>
        </div>

        <div className={'vspace-between-2'}>
            {
                sortedNiceData.length > 0
                    ? sortedNiceData.map(data =>
                        <div key={data.baseSpeed}>
                            {translate('minitool/trav/speed_analysis/base_speed')} {data.baseSpeed} {translate('travian/tournament_square_short')}{data.min} - {data.max}
                        </div>
                    )
                    : translate('minitool/trav/speed_analysis/no_results')
            }
        </div>
    </div>
}
